import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CreditCard, PaymentForm, GooglePay } from 'react-square-web-payments-sdk';
import { clearCart } from '../services/cartSlice';
import API from '../services/API';
import CartEmpty from '../components/checkout/CartEmpty';
import OrderSummary from '../components/checkout/OrderSummary';
import ShippingForm from '../components/checkout/ShippingForm';
import { useCheckout } from '../services/useCheckout';

const Checkout = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentFormKey, setPaymentFormKey] = useState(1);
  const items = useSelector(state => state.cart.items);
  const total = useSelector(state => state.cart.total);
  const { formData, handleChange, validateForm } = useCheckout();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Calculate totals
  const subtotal = total;
  const shippingCost = formData.shippingMethod?.rate || 0;
  const taxRate = 0.05; // 5% tax
  const taxAmount = subtotal * taxRate;
  const finalTotal = subtotal + shippingCost + taxAmount;

  // Increment key when finalTotal changes to force re-render
  useEffect(() => {
    setPaymentFormKey(prev => prev + 1);
  }, [finalTotal]);

  const handlePaymentSuccess = async (token, verifiedBuyer) => {
    if (!validateForm()) {
      setPaymentError('Please ensure all required fields are filled correctly:' + 
        '\n- All required fields must be filled' +
        '\n- Email must be valid' +
        '\n- Phone number must be valid' +
        '\n- Shipping method must be selected');
      return;
    }

    if (!formData.shippingMethod) {
      setPaymentError('Please select a shipping method');
      return;
    }
    
    setIsProcessing(true);
    setPaymentError(null);
    console.log({
      sourceId: token.token,
      amount: Math.round(finalTotal * 100), // Convert to cents
      shipping: formData.shippingMethod,
      billingDetails: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        streetAddress: formData.streetAddress,
        apartment: formData.apartment,
        city: formData.city,
        province: formData.province,
        country: formData.country,
        postalCode: formData.postalCode,
        phone: formData.phone
      },
      items: items.map(item => ({
        id: item.id,
        quantity: item.quantity,
        price: item.price,
        name: item.name
      }))
    })

    try {
      const result = await API.processPayment({
        sourceId: token.token,
        amount: Math.round(finalTotal * 100), // Convert to cents
        shipping: formData.shippingMethod,
        shippingMethod: formData.shippingMethod.service,
        billingDetails: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          streetAddress: formData.streetAddress,
          apartment: formData.apartment,
          city: formData.city,
          province: formData.province,
          country: formData.country,
          postalCode: formData.postalCode,
          phone: formData.phone
        },
        items: items.map(item => ({
          id: item.id,
          quantity: item.quantity,
          price: item.price,
          name: item.name
        }))
      });
      
      if (result.success) {
        dispatch(clearCart());
        navigate('/order-confirmation', { 
          state: { 
            orderId: result.payment.id,
            amount: finalTotal,
            items: items
          }
        });
      } else {
        setPaymentError(result.error || 'Payment processing failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError('An error occurred while processing your payment');
    } finally {
      setIsProcessing(false);
    }
  };

  if (items.length === 0) {
    return <CartEmpty />;
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Checkout</h2>

      <ShippingForm 
        formData={formData}
        handleChange={handleChange}
      />

      <OrderSummary 
        items={items} 
        taxAmount={taxAmount}
        subtotal={total} 
        shippingRate={formData.shippingMethod}
        total={finalTotal}
      />

      <div className="mt-6 border rounded-lg p-6 bg-white">
        <h3 className="text-lg font-semibold mb-4">Payment Details</h3>
        <PaymentForm
          key={paymentFormKey}
          applicationId={process.env.REACT_APP_SQUARE_APPLICATION_ID}
          locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
          cardTokenizeResponseReceived={handlePaymentSuccess}
          createPaymentRequest={() => ({
            countryCode: "CA",
            currencyCode: "CAD",
            total: {
              amount: finalTotal.toFixed(2),
              label: "Total"
            }
          })}
        >
          <GooglePay/>
          <CreditCard 
            buttonProps={{
              isLoading: isProcessing,
              css: {
                backgroundColor: '#4F46E5',
                fontSize: '16px',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#4338CA',
                },
              },
            }}
          >
            {isProcessing ? 'Processing...' : `Pay CA$${finalTotal.toFixed(2)}`}
          </CreditCard>
        </PaymentForm>
      </div>

      {paymentError && (
        <div className="mt-4 p-3 bg-red-100 text-red-700 rounded whitespace-pre-line">
          {paymentError}
        </div>
      )}
    </div>
  );
};

export default Checkout;